import { lazy, Suspense, useLayoutEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "./context/authContext";

// Layout
import DashboardLayout from "./layout/DashboardLayout";

// Rutas protegidas
import ProtectedRoute from "./routes/protectedRoute";
import RoleRoute from "./routes/roleRoute";
import HomeRoute from "./routes/homeRoute";
import { CRM_ROLES } from "./pages/CRM/hooks/roles";

// Cada pantalla se descarga al entrar. Así el arranque no trae
// calendario, Excel, PDF ni OCR de páginas que el usuario no abre.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Calendario = lazy(() => import("./pages/Calendario/index"));
const DistributionReport = lazy(() => import("./pages/DistributionReport"));
const Clientes = lazy(() => import("./pages/Clientes/index"));
const Analisis = lazy(() => import("./pages/Analisis/index"));
const AnalisisModulo = lazy(() => import("./pages/Analisis/Modulo"));
const Documentos = lazy(() => import("./pages/Documentos"));
const Verificacion = lazy(() => import("./pages/Verificacion"));
const Tecnicos = lazy(() => import("./pages/Tecnicos"));
const Inventario = lazy(() => import("./pages/Inventario"));
const Login = lazy(() => import("./pages/Login/index"));
const Servicios = lazy(() => import("./pages/Servicios/index"));
const CRM = lazy(() => import("./pages/CRM/index"));
const CrmAdmin = lazy(() => import("./pages/CRM/AdminView"));
const CrmExecutive = lazy(() => import("./pages/CRM/ExecutiveView"));
const Rendimiento = lazy(() => import("./pages/Rendimiento"));
const Programacion = lazy(() => import("./pages/Programacion"));
const Sucursal = lazy(() => import("./pages/Sucursal"));
const ActivityTracker = lazy(() => import("./components/ActivityTracker"));
const Mantenimiento = lazy(() => import("./pages/Mantenimiento"));
const Seguridad = lazy(() => import("./pages/Seguridad"));

function AppearanceGate() {
  const { pathname } = useLocation();
  const { user } = useAuth();

  useLayoutEffect(() => {
    const onLogin = pathname.startsWith("/login");
    let stored = false;
    try {
      stored = localStorage.getItem("trazia-theme") === "dark";
    } catch {
      stored = false;
    }
    const dark = user?.appearance ? user.appearance === "dark" : stored;
    document.documentElement.dataset.theme = !onLogin && dark ? "dark" : "light";
  }, [pathname, user?.appearance]);

  return null;
}

export default function App() {
  return (
    <>
      <AppearanceGate />
      {/* Rastreador de actividad - Se ejecuta en toda la aplicación */}
      <ActivityTracker />
      
      <Suspense fallback={null}>
      <Routes>
        {/* Login sin layout */}
        <Route path="/login" element={<Login />} />

        {/* Rutas protegidas con layout */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>

          {/* Admin L3 */}
          <Route element={<RoleRoute allowedRoles={["admin"]} />}>
            <Route path="/seguridad" element={<Seguridad />} />
          </Route>

          <Route path="/analisis-demo" element={<Analisis />} />

          <Route element={<RoleRoute allowedRoles={["admin", "dev"]} />}>
            <Route path="/analisis" element={<AnalisisModulo />} />
            <Route path="/rendimiento" element={<Rendimiento />} />
            <Route path="/programacion" element={<Programacion />} />

            {/* Rutas vacias */}
            <Route path="/verificacion" element={<Verificacion />} />
            <Route path="/documentos" element={<Documentos />} />
          </Route>


          {/* Supervisión L2 */}
          <Route element={<RoleRoute allowedRoles={["admin", "rh", "supervisor"]} />}>
            <Route path="/inventario" element={<Inventario />} />
            <Route path="/tecnicos" element={<Tecnicos />} />
          </Route>

          {/* Administración L1 */}
          <Route element={<RoleRoute allowedRoles={["admin", "scheduler", "rh", "supervisor"]} />}>
            <Route path="/calendario" element={<Calendario />} />
            <Route path="/servicios" element={<Servicios />} />
            <Route path="/cloud" element={<Mantenimiento />} />
            <Route path="/clientes" element={<Clientes />} />
            <Route path="/distribucion" element={<DistributionReport />} />
            <Route path="/sucursales/:id" element={<Sucursal />} />
          </Route>

          {/* CRM */}
          <Route element={<RoleRoute allowedRoles={CRM_ROLES} />}>
            <Route path="/crm" element={<CRM />} />
          </Route>
          <Route element={<RoleRoute allowedRoles={["crm_manager"]} />}>
            <Route path="/crm/admin" element={<CrmAdmin />} />
          </Route>
          <Route element={<RoleRoute allowedRoles={["crm_agent", "crm_marketer", "crm_qualifier"]} />}>
            <Route path="/crm/ejecutivo" element={<CrmExecutive />} />
          </Route>

          <Route path="/" element={<Dashboard />} />

          {/* Fallback */}
          <Route path="*" element={<HomeRoute />} />
          </Route>
        </Route>
      </Routes>
      </Suspense>
    </>
  );
}